import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Box,
  useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [radius, setRadius] = useState(150);
  const navigate = useNavigate();
  const isShort = useMediaQuery('(max-height:550px)', { noSsr: true });

  // AbortController ref
  const searchAbortControllerRef = useRef();

  const handleSearch = async () => {
    // cancel any in-flight search
    searchAbortControllerRef.current?.abort();

    const trimmed = query.trim();
    if (!trimmed) {
      navigate('/search', { state: { results: [], notFound: true } });
      return;
    }

    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    try {
      const res = await api.get(
        `/api/proxy/search?q=${encodeURIComponent(trimmed)}&radius=${radius}`,
        { signal: controller.signal }
      );
      const results = res.data;
      navigate('/search', {
        state: { results, notFound: results.length === 0 },
      });
    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error(err);
        navigate('/search', { state: { results: [], notFound: true } });
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      searchAbortControllerRef.current?.abort();
    };
  }, []);

  return (
    <Box
      sx={{
        width: '600px',
        mx: 'auto',
      }}
    >
      <TextField
        fullWidth
        placeholder="Search..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        variant="outlined"
        InputProps={{
          sx: {
            height: isShort ? 36 : 'auto',
            '& .MuiInputBase-root': {
              padding: isShort ? '4px 8px' : undefined,
            },
            backgroundColor: '#f5f5f5',
            '&:hover': { backgroundColor: '#f0f0f0' },
            '&.Mui-focused': { backgroundColor: '#ffffff' },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          },
          startAdornment: (
            <InputAdornment position="start">
              <Select
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                variant="standard"
                disableUnderline
                sx={{
                  m: 0,
                  mr: 1,
                  backgroundColor: 'transparent',
                  '& .MuiSelect-select': {
                    backgroundColor: 'transparent !important',
                  },
                  fontSize: isShort ? '0.75rem' : undefined,
                }}
              >
                <MenuItem value={0}>OFF</MenuItem>
                {[...Array(40)].map((_, i) => {
                  const val = (i + 1) * 5;
                  return val > 200 ? null : (
                    <MenuItem key={val} value={val}>
                      {val}m
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearch} size={isShort ? 'small' : 'medium'}>
                <SearchIcon fontSize={isShort ? 'small' : 'default'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default SearchBar;
